'use client';
import Button from '@ui/Button';
import React, { useCallback } from 'react';
import {
  isFollowingUser,
  toggleFollowInfluencer,
} from '../../stores/userActions';
import { getFullName } from '../../utils/stringHelpers';
import useConfirmDialogStore from '../Dialogs/confirmDialog.store';
import { AiFillCheckCircle, AiOutlinePlusCircle } from 'react-icons/ai';
import { cva, cx } from 'cva';

const followUserButton = cva('follow-user-button', {
  variants: {
    size: {
      xs: ['w-4 h-4'],
      sm: ['w-6 h-6'],
      md: ['w-9 h-9'],
      lg: ['w-12 h-12'],
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

function FollowUserButton(props) {
  const {
    user,
    viewer = null,
    onToggle = () => {},
    disabled = false,
    noConfirm = false,
    revalidate = () => {},
    type = null,
    size = 'md',
    ...rest
  } = props;

  const iconStyles = followUserButton({ size });

  const [isFollowing, setIsFollowing] = React.useState(disabled || false);
  const [busy, setBusy] = React.useState(false);
  const [hovering, setHovering] = React.useState(false);
  const { seekConfirmation } = useConfirmDialogStore();
  const currentUser = viewer;

  const checkFollowing = useCallback(() => {
    if (user) {
      return isFollowingUser(user.id);
    }
    return false;
  }, [user]);

  React.useEffect(() => {
    const following = checkFollowing();
    setIsFollowing(following);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkFollowing]);

  const handleClick = async (e) => {
    e.stopPropagation();
    if (!busy && !disabled) {
      const title = `Unfollow ${getFullName(user)}?`;
      const message = 'You will no longer receive updates from this contact.';
      const confirmed = await (isFollowing && !noConfirm
        ? seekConfirmation(title, message, 'Unfollow')
        : true);
      if (confirmed) {
        setBusy(true);
        const promise = toggleFollowInfluencer(user.id);
        setIsFollowing(checkFollowing());
        const following = await promise;
        setIsFollowing(following);
        setBusy(false);
        onToggle(following);
      }
    }
    revalidate();
  };

  const canFollow = currentUser && user.canFollow;

  if (canFollow) {
    if (type === 'plus') {
      return (
        <button onClick={handleClick} disabled={busy || disabled} {...rest}>
          {isFollowing ? (
            <AiFillCheckCircle
              className={cx(
                iconStyles,
                busy || disabled ? 'text-text-disabled' : 'text-primary',
              )}
            />
          ) : (
            <AiOutlinePlusCircle className={cx(iconStyles, 'text-primary')} />
          )}
        </button>
      );
    }
    return isFollowing ? (
      <Button
        variant="filled"
        color="primary"
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        onClick={handleClick}
        disabled={busy}
        className="min-w-[131px]"
        {...rest}>
        {hovering ? 'Unfollow' : 'Following'}
      </Button>
    ) : (
      <Button
        variant="filled"
        color="primary"
        onClick={handleClick}
        disabled={busy}
        {...rest}>
        Follow
      </Button>
    );
  }
  return null;
}

export default FollowUserButton;
